import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { API } from "@/context/AuthContext";
import { useParams, useNavigate } from "react-router-dom";
import {
  ArrowLeft, Plus, Edit2, Trash2, ChevronDown, ChevronRight, Bug, BookOpen,
  Layers, Zap, CheckSquare, X, Loader2, Send, Bell, LayoutGrid, List,
  Clock, AlertTriangle, Flag, Target, GitBranch, Calendar, TrendingDown, ChevronLeft,
  CloudUpload, CheckCircle2, ExternalLink, Tag, Hash, Info, MessageSquare
} from "lucide-react";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ReferenceLine, ResponsiveContainer,
  ComposedChart, Bar
} from "recharts";
import { ProjectBurndownPanel, ProjectMilestones } from "@/components/project/ProjectPanels";
import ProjectMeetings from "@/components/projects/ProjectMeetings";

const statusColor = { active: "status-active", paused: "status-paused", completed: "status-completed", blocked: "status-blocked", Activo: "status-active", Nuevo: "bg-gray-100 text-gray-600", Resuelto: "status-completed", Cerrado: "status-completed" };
const priorityColor = { critica: "priority-critica", alta: "priority-alta", media: "priority-media", baja: "priority-baja" };
const typeIcon = { Epic: Layers, Feature: Zap, "User Story": BookOpen, Bug: Bug, Task: CheckSquare, Tarea: CheckSquare };

const KANBAN_COLS = [
  { key: "todo",   label: "Por hacer",   statuses: ["Nuevo","New"],                          newStatus: "Nuevo",   headerCls: "bg-blue-50 text-blue-700 border-blue-200",   dotCls: "bg-blue-400" },
  { key: "doing",  label: "En curso",    statuses: ["Activo","Active","Analyzing"],           newStatus: "Activo",  headerCls: "bg-amber-50 text-amber-700 border-amber-200", dotCls: "bg-amber-400" },
  { key: "review", label: "En revisión", statuses: ["Resolved","Resuelto"],                  newStatus: "Resuelto",headerCls: "bg-purple-50 text-purple-700 border-purple-200",dotCls: "bg-purple-400" },
  { key: "done",   label: "Cerrado",     statuses: ["Closed","closed","Cerrado","Done","Completado","Removed"], newStatus: "Cerrado", headerCls: "bg-green-50 text-green-700 border-green-200", dotCls: "bg-green-400" },
];

const SPRINT_STATUS_CFG = {
  planning:  { label: "Planificación", cls: "bg-blue-100 text-blue-700" },
  active:    { label: "Activo",        cls: "bg-green-100 text-green-700" },
  completed: { label: "Completado",    cls: "bg-gray-100 text-gray-600" },
};

const MS_STATUS_CFG = {
  pending:     { label: "Pendiente",    cls: "bg-gray-100 text-gray-600",   dot: "bg-gray-400" },
  in_progress: { label: "En progreso",  cls: "bg-amber-100 text-amber-700", dot: "bg-amber-400" },
  completed:   { label: "Completado",   cls: "bg-green-100 text-green-700", dot: "bg-green-500" },
  delayed:     { label: "Retrasado",    cls: "bg-red-100 text-red-700",     dot: "bg-red-500" },
};

export default function ProjectDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [project, setProject] = useState(null);
  const [workitems, setWorkitems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({});
  const [showWIModal, setShowWIModal] = useState(false);
  const [wiForm, setWiForm] = useState({ title: "", type: "Task", status: "Nuevo", priority: "media", assigned_to: "", estimated_hours: 0, description: "", sprint_id: "" });
  const [saving, setSaving] = useState(false);
  const [reminderEmail, setReminderEmail] = useState("");
  const [sending, setSending] = useState(false);
  const [viewMode, setViewMode] = useState("list");
  const [dragId, setDragId] = useState(null);

  // Work Item detail panel
  const [selectedWI, setSelectedWI] = useState(null);
  const [wiEditForm, setWiEditForm] = useState({});
  const [wiPanelSaving, setWiPanelSaving] = useState(false);
  const [wiPushing, setWiPushing] = useState(false);
  const [wiPublishing, setWiPublishing] = useState(false);
  const [wiSyncMsg, setWiSyncMsg] = useState(null); // {ok, text}

  // Notes
  const [wiNoteText, setWiNoteText] = useState("");
  const [wiAddingNote, setWiAddingNote] = useState(false);

  // Tabs: workitems | sprints | milestones
  const [activeTab, setActiveTab] = useState("workitems");

  // Sprints
  const [sprints, setSprints] = useState([]);
  const [sprintBoard, setSprintBoard] = useState(null);
  const [showSprintModal, setShowSprintModal] = useState(false);
  const [sprintForm, setSprintForm] = useState({ name: "", number: 1, status: "planning", start_date: "", end_date: "", goal: "" });
  const [sprintDragId, setSprintDragId] = useState(null);
  const [sprintDragTarget, setSprintDragTarget] = useState(null);

  // Milestones
  const [milestones, setMilestones] = useState([]);
  const [showMSModal, setShowMSModal] = useState(false);
  const [msForm, setMsForm] = useState({ title: "", description: "", due_date: "", status: "pending" });
  const [editingMS, setEditingMS] = useState(null);

  // Burndown chart
  const [burndownSprint, setBurndownSprint] = useState(null);  // selected sprint for burndown
  const [burndownData, setBurndownData] = useState(null);
  const [burndownLoading, setBurndownLoading] = useState(false);

  // Velocity chart
  const [velocityData, setVelocityData] = useState([]);

  const load = useCallback(async () => {
    try {
      const [pRes, wiRes, sprintsRes] = await Promise.all([
        axios.get(`${API}/projects/${id}`),
        axios.get(`${API}/projects/${id}/workitems`),
        axios.get(`${API}/projects/${id}/sprints`),
      ]);
      setProject(pRes.data);
      setEditForm(pRes.data);
      setWorkitems(wiRes.data);
      setSprints(sprintsRes.data);
    } catch (e) { navigate("/projects"); }
    finally { setLoading(false); }
  }, [id, navigate]);

  useEffect(() => { load(); }, [load]);

  // Load sprints/milestones on tab switch
  useEffect(() => {
    if (activeTab === "sprints" && project) {
      axios.get(`${API}/projects/${id}/sprint-board`).then(r => setSprintBoard(r.data)).catch(console.error);
      axios.get(`${API}/projects/${id}/sprints`).then(r => setSprints(r.data)).catch(console.error);
      axios.get(`${API}/projects/${id}/velocity`).then(r => setVelocityData(r.data)).catch(console.error);
    }
    if (activeTab === "milestones" && project) {
      axios.get(`${API}/projects/${id}/milestones`).then(r => setMilestones(r.data)).catch(console.error);
    }
  }, [activeTab, project, id]);

  const handleSaveProject = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/projects/${id}`, editForm);
      setProject({ ...project, ...editForm });
      setEditing(false);
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleCreateWI = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/workitems`, { ...wiForm, project_id: id });
      setWorkitems(prev => [...prev, data]);
      setShowWIModal(false);
      setWiForm({ title: "", type: "Task", status: "Nuevo", priority: "media", assigned_to: "", estimated_hours: 0, description: "", sprint_id: "" });
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleOpenWIPanel = (wi) => {
    setSelectedWI(wi);
    setWiEditForm({ ...wi });
    setWiSyncMsg(null);
  };

  const handleCloseWIPanel = () => {
    setSelectedWI(null);
    setWiEditForm({});
    setWiSyncMsg(null);
    setWiNoteText("");
  };

  const handleSaveWIEdit = async () => {
    setWiPanelSaving(true);
    try {
      await axios.put(`${API}/workitems/${selectedWI.id}`, wiEditForm);
      setWorkitems(prev => prev.map(w => w.id === selectedWI.id ? { ...w, ...wiEditForm } : w));
      setSelectedWI(prev => ({ ...prev, ...wiEditForm }));
      setWiSyncMsg({ ok: true, text: "Cambios guardados correctamente" });
      setTimeout(() => setWiSyncMsg(null), 3000);
    } catch (e) { console.error(e); setWiSyncMsg({ ok: false, text: "Error al guardar" }); }
    finally { setWiPanelSaving(false); }
  };

  const handleQuickCompleteFromPanel = async () => {
    setWiPanelSaving(true);
    try {
      const updated = { ...wiEditForm, status: "Cerrado" };
      await axios.put(`${API}/workitems/${selectedWI.id}`, { status: "Cerrado" });
      setWorkitems(prev => prev.map(w => w.id === selectedWI.id ? { ...w, status: "Cerrado" } : w));
      setWiEditForm(updated);
      setSelectedWI(prev => ({ ...prev, status: "Cerrado" }));
      setWiSyncMsg({ ok: true, text: "Marcado como completado" });
      setTimeout(() => setWiSyncMsg(null), 3000);
    } catch (e) { console.error(e); }
    finally { setWiPanelSaving(false); }
  };

  const handleQuickCompleteItem = async (e, wi) => {
    e.stopPropagation();
    if (["Cerrado","Closed","Done","Completado","Removed"].includes(wi.status)) return;
    await axios.put(`${API}/workitems/${wi.id}`, { status: "Cerrado" });
    setWorkitems(prev => prev.map(w => w.id === wi.id ? { ...w, status: "Cerrado" } : w));
  };

  const handlePushToDevops = async () => {
    setWiPushing(true);
    setWiSyncMsg(null);
    try {
      // Save locally first
      await axios.put(`${API}/workitems/${selectedWI.id}`, wiEditForm);
      setWorkitems(prev => prev.map(w => w.id === selectedWI.id ? { ...w, ...wiEditForm } : w));
      // Then push to DevOps
      const { data } = await axios.post(`${API}/workitems/${selectedWI.id}/push-to-devops`);
      setWiSyncMsg({ ok: true, text: `Enviado a Azure DevOps (ID #${selectedWI.azure_id})` });
      setWorkitems(prev => prev.map(w => w.id === selectedWI.id ? { ...w, ...wiEditForm, devops_synced_at: data.synced_at } : w));
      setSelectedWI(prev => ({ ...prev, ...wiEditForm, devops_synced_at: data.synced_at }));
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al enviar a DevOps";
      setWiSyncMsg({ ok: false, text: msg });
    } finally { setWiPushing(false); }
  };

  const handlePublishToDevops = async () => {
    setWiPublishing(true);
    setWiSyncMsg(null);
    try {
      await axios.put(`${API}/workitems/${selectedWI.id}`, wiEditForm);
      const { data } = await axios.post(`${API}/workitems/${selectedWI.id}/create-in-devops`);
      const updated = { ...wiEditForm, azure_id: data.azure_id, azure_project: data.azure_project, devops_synced_at: data.synced_at };
      setWorkitems(prev => prev.map(w => w.id === selectedWI.id ? { ...w, ...updated } : w));
      setWiEditForm(updated);
      setSelectedWI(prev => ({ ...prev, ...updated }));
      setWiSyncMsg({ ok: true, text: `Publicado en Azure DevOps con ID #${data.azure_id}` });
    } catch (e) {
      const msg = e.response?.data?.detail || "Error al publicar en DevOps";
      setWiSyncMsg({ ok: false, text: msg });
    } finally { setWiPublishing(false); }
  };

  const handleAddNote = async () => {
    const text = wiNoteText.trim();
    if (!text) return;
    setWiAddingNote(true);
    try {
      const { data } = await axios.post(`${API}/workitems/${selectedWI.id}/notes`, { text });
      const updatedNotes = [data, ...(wiEditForm.notes || [])];
      setWiEditForm(prev => ({ ...prev, notes: updatedNotes }));
      setSelectedWI(prev => ({ ...prev, notes: updatedNotes }));
      setWorkitems(prev => prev.map(w => w.id === selectedWI.id ? { ...w, notes: updatedNotes } : w));
      setWiNoteText("");
    } catch (e) { console.error(e); }
    finally { setWiAddingNote(false); }
  };

  const handleDeleteNote = async (noteId) => {
    try {
      await axios.delete(`${API}/workitems/${selectedWI.id}/notes/${noteId}`);
      const updatedNotes = (wiEditForm.notes || []).filter(n => n.id !== noteId);
      setWiEditForm(prev => ({ ...prev, notes: updatedNotes }));
      setSelectedWI(prev => ({ ...prev, notes: updatedNotes }));
      setWorkitems(prev => prev.map(w => w.id === selectedWI.id ? { ...w, notes: updatedNotes } : w));
    } catch (e) { console.error(e); }
  };

  const handleDeleteWI = async (wid) => {
    if (!window.confirm("¿Eliminar este work item?")) return;
    await axios.delete(`${API}/workitems/${wid}`);
    setWorkitems(prev => prev.filter(w => w.id !== wid));
  };

  const handleSendReminder = async () => {
    if (!reminderEmail) return;
    setSending(true);
    try {
      await axios.post(`${API}/projects/${id}/remind`, { email: reminderEmail });
      setReminderEmail("");
      alert("Recordatorio enviado correctamente");
    } catch (e) { console.error(e); }
    finally { setSending(false); }
  };

  // Sprint CRUD
  const handleCreateSprint = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const { data } = await axios.post(`${API}/sprints`, { ...sprintForm, project_id: id, number: parseInt(sprintForm.number) });
      setSprints(prev => [...prev, data]);
      setSprintBoard(prev => prev ? { ...prev, sprints: [...prev.sprints, { ...data, work_items: [] }] } : null);
      setShowSprintModal(false);
      setSprintForm({ name: "", number: sprints.length + 2, status: "planning", start_date: "", end_date: "", goal: "" });
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDeleteSprint = async (sid) => {
    if (!window.confirm("¿Eliminar este sprint? Los work items quedarán sin asignar.")) return;
    await axios.delete(`${API}/sprints/${sid}`);
    setSprints(prev => prev.filter(s => s.id !== sid));
    setSprintBoard(prev => prev ? { ...prev, sprints: prev.sprints.filter(s => s.id !== sid) } : null);
  };

  const handleUpdateSprintStatus = async (sid, newStatus) => {
    await axios.put(`${API}/sprints/${sid}`, { status: newStatus });
    setSprints(prev => prev.map(s => s.id === sid ? { ...s, status: newStatus } : s));
  };

  // Drag work items between sprints
  const handleDropOnSprint = async (targetSprintId) => {
    if (!sprintDragId) return;
    try {
      await axios.put(`${API}/workitems/${sprintDragId}`, { sprint_id: targetSprintId });
      // Refresh sprint board
      const { data } = await axios.get(`${API}/projects/${id}/sprint-board`);
      setSprintBoard(data);
    } catch (e) { console.error(e); }
    setSprintDragId(null);
    setSprintDragTarget(null);
  };

  // Milestones CRUD
  const handleSaveMS = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editingMS) {
        const { data } = await axios.put(`${API}/milestones/${editingMS.id}`, msForm);
        setMilestones(prev => prev.map(m => m.id === editingMS.id ? data : m));
      } else {
        const { data } = await axios.post(`${API}/milestones`, { ...msForm, project_id: id });
        setMilestones(prev => [...prev, data].sort((a, b) => a.due_date.localeCompare(b.due_date)));
      }
      setShowMSModal(false);
      setEditingMS(null);
      setMsForm({ title: "", description: "", due_date: "", status: "pending" });
    } catch (e) { console.error(e); }
    finally { setSaving(false); }
  };

  const handleDeleteMS = async (mid) => {
    if (!window.confirm("¿Eliminar este hito?")) return;
    await axios.delete(`${API}/milestones/${mid}`);
    setMilestones(prev => prev.filter(m => m.id !== mid));
  };

  const handleUpdateMSStatus = async (mid, newStatus) => {
    await axios.put(`${API}/milestones/${mid}`, { status: newStatus });
    setMilestones(prev => prev.map(m => m.id === mid ? { ...m, status: newStatus } : m));
  };

  const openEditMS = (ms) => {
    setEditingMS(ms);
    setMsForm({ title: ms.title, description: ms.description || "", due_date: ms.due_date, status: ms.status });
    setShowMSModal(true);
  };

  const handleOpenBurndown = async (sprint) => {
    setBurndownSprint(sprint);
    setBurndownData(null);
    setBurndownLoading(true);
    try {
      const { data } = await axios.get(`${API}/sprints/${sprint.id}/burndown`);
      setBurndownData(data);
    } catch (e) { console.error(e); }
    finally { setBurndownLoading(false); }
  };

  if (loading) return <div className="flex items-center justify-center h-64"><Loader2 className="animate-spin text-[#007934]" size={32} /></div>;
  if (!project) return null;

  const completedWI = workitems.filter(w => ["Closed","closed","Cerrado","Done","Completado","Removed"].includes(w.status)).length;
  const workitemsConEstimacion = workitems.filter(w => (w.estimated_hours || 0) > 0);
  const totalHours = workitemsConEstimacion.reduce((s, w) => s + w.estimated_hours, 0);
  // N03 (roadmap-valoriza-jev-2026-10-11) · "sin estimar" (N/D) no es lo mismo
  // que "estimado en cero" — si NINGÚN work item tiene una estimación real,
  // mostrar N/D en vez de "0h" (que se leería como "no requiere trabajo").
  const horasEstDisplay = workitems.length > 0 && workitemsConEstimacion.length === 0
    ? "N/D" : `${totalHours}h`;
  const epics = workitems.filter(w => w.type === "Epic");

  return (
    <div className="space-y-5">
      {/* Back button */}
      <button onClick={() => navigate("/projects")} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 transition-colors">
        <ArrowLeft size={16} /> Volver a Proyectos
      </button>

      {/* Project card */}
      <div className="bg-white rounded-xl border border-gray-100 p-5 space-y-4">
        {editing ? (
          <div className="space-y-4">
            <h2 className="text-lg font-bold font-['Outfit']">Editar proyecto</h2>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div className="sm:col-span-2">
                <label className="text-xs font-medium text-gray-600 mb-1 block">Título</label>
                <input value={editForm.title || ""} onChange={e => setEditForm({...editForm, title: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Estado</label>
                <select value={editForm.status || "active"} onChange={e => setEditForm({...editForm, status: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                  {["active","paused","completed","blocked"].map(s => <option key={s}>{s}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Prioridad</label>
                <select value={editForm.priority || "media"} onChange={e => setEditForm({...editForm, priority: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                  {["critica","alta","media","baja"].map(p => <option key={p}>{p}</option>)}
                </select>
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Responsable Técnico</label>
                <input value={editForm.responsible_tech || ""} onChange={e => setEditForm({...editForm, responsible_tech: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Responsable Negocio</label>
                <input value={editForm.responsible_business || ""} onChange={e => setEditForm({...editForm, responsible_business: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Área</label>
                <input value={editForm.area || ""} onChange={e => setEditForm({...editForm, area: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Departamento</label>
                <input value={editForm.department || ""} onChange={e => setEditForm({...editForm, department: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Presupuesto asignado (€)</label>
                <input type="number" value={editForm.budget_allocated || 0} onChange={e => setEditForm({...editForm, budget_allocated: parseFloat(e.target.value)})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Presupuesto gastado (€)</label>
                <input type="number" value={editForm.budget_spent || 0} onChange={e => setEditForm({...editForm, budget_spent: parseFloat(e.target.value)})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Fecha inicio</label>
                <input type="date" value={editForm.start_date || ""} onChange={e => setEditForm({...editForm, start_date: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Fecha límite</label>
                <input type="date" value={editForm.end_date || ""} onChange={e => setEditForm({...editForm, end_date: e.target.value})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
              <div>
                <label className="text-xs font-medium text-gray-600 mb-1 block">Días estimados</label>
                <input type="number" value={editForm.estimated_days || 0} onChange={e => setEditForm({...editForm, estimated_days: parseInt(e.target.value)})}
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
              </div>
            </div>
            <div className="flex gap-2">
              <button onClick={handleSaveProject} disabled={saving} className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center gap-2" data-testid="save-edit-btn">
                {saving ? <Loader2 size={14} className="animate-spin" /> : null} Guardar cambios
              </button>
              <button onClick={() => setEditing(false)} className="border border-gray-200 text-gray-700 px-4 py-2 rounded-lg text-sm font-medium hover:bg-gray-50">Cancelar</button>
            </div>
          </div>
        ) : (
          <>
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1">
                <div className="flex items-center gap-3 flex-wrap">
                  <h1 className="text-xl font-bold text-gray-900 font-['Outfit']">{project.title}</h1>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${statusColor[project.status] || ""}`}>{project.status}</span>
                  <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${priorityColor[project.priority] || ""}`}>{project.priority}</span>
                  <span className="text-xs bg-blue-50 text-blue-700 px-2 py-0.5 rounded-full font-medium uppercase">{project.type}</span>
                </div>
                <p className="text-sm text-gray-500 mt-1">{project.area} · {project.department}</p>
                {project.description && <p className="text-sm text-gray-600 mt-2 leading-relaxed max-w-2xl">{project.description}</p>}
              </div>
              <button onClick={() => setEditing(true)} className="flex items-center gap-1.5 text-sm text-gray-500 hover:text-gray-700 border border-gray-200 px-3 py-1.5 rounded-lg hover:bg-gray-50" data-testid="edit-project-btn">
                <Edit2 size={14} /> Editar
              </button>
            </div>
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mt-4 pt-4 border-t border-gray-50">
              {[
                ["Responsable Técnico", project.responsible_tech || "—"],
                ["Responsable Negocio", project.responsible_business || "—"],
                // Iter313 · O05 — un proyecto type="commercial" (vinculado a
                // oferta ganada) tiene el PBL del contrato en este campo,
                // no un presupuesto IT — se etiqueta para no confundirlo.
                [project.type === "commercial" ? "PBL del contrato (no es presupuesto IT)" : "Presupuesto", project.budget_allocated ? `${project.budget_allocated.toLocaleString("es-ES")}€` : "—"],
                ["Fecha límite", project.end_date || "—"],
              ].map(([label, val]) => (
                <div key={label}>
                  <p className="text-xs text-gray-400 uppercase tracking-wider">{label}</p>
                  <p className="text-sm font-semibold text-gray-800 mt-0.5">{val}</p>
                </div>
              ))}
            </div>
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-gray-500 mb-1.5">
                <span>Progreso del proyecto</span>
                <span className="font-semibold text-[#007934]">{project.progress || 0}%</span>
              </div>
              <div className="h-2 bg-gray-100 rounded-full">
                <div className="h-full bg-gradient-to-r from-[#007934] to-[#4CAF50] rounded-full transition-all" style={{ width: `${project.progress || 0}%` }} data-testid="project-progress-bar" />
              </div>
            </div>
            {project.estimated_delivery?.status === "calculado" && (
              <div className="mt-3 flex items-center gap-3 bg-[#F0FDF4] border border-[#007934]/20 rounded-lg px-4 py-2.5" data-testid="estimated-delivery-banner">
                <Clock size={15} className="text-[#007934] flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-[#007934] font-semibold">Entrega estimada (dinámica): </span>
                  <span className="text-sm font-bold text-gray-900">{new Date(project.estimated_delivery.estimated_date).toLocaleDateString("es-ES", { day:"2-digit", month:"short", year:"numeric" })}</span>
                  <span className="text-xs text-gray-500 ml-2">· {project.estimated_delivery.open_items} items abiertos · {project.estimated_delivery.weeks_needed} semanas · {project.estimated_delivery.assigned_resources} recursos</span>
                </div>
              </div>
            )}
            {project.estimated_delivery?.status === "calculado_cobertura_parcial" && (
              <div className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5" data-testid="estimated-delivery-banner-parcial">
                <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />
                <div className="flex-1 min-w-0">
                  <span className="text-xs text-amber-700 font-semibold">Entrega estimada (cobertura parcial): </span>
                  <span className="text-sm font-bold text-gray-900">{new Date(project.estimated_delivery.estimated_date).toLocaleDateString("es-ES", { day:"2-digit", month:"short", year:"numeric" })}</span>
                  <span className="text-xs text-amber-700 ml-2">· calculada solo con {project.estimated_delivery.open_items - project.estimated_delivery.items_sin_estimar} de {project.estimated_delivery.open_items} items abiertos ({project.estimated_delivery.items_sin_estimar} sin estimación de horas, excluidos del cálculo — no se asume ningún valor por defecto)</span>
                </div>
              </div>
            )}
            {project.estimated_delivery?.status === "sin_estimacion_valida" && (
              <div className="mt-3 flex items-center gap-3 bg-gray-50 border border-gray-200 rounded-lg px-4 py-2.5" data-testid="estimated-delivery-banner-sin-estimacion">
                <AlertTriangle size={15} className="text-gray-400 flex-shrink-0" />
                <span className="text-xs text-gray-600">Fecha de entrega: N/D — ninguno de los {project.estimated_delivery.open_items} items abiertos tiene una estimación de horas válida</span>
              </div>
            )}
            {project.estimated_delivery?.status === "sin_recursos" && (
              <div className="mt-3 flex items-center gap-3 bg-amber-50 border border-amber-200 rounded-lg px-4 py-2.5">
                <AlertTriangle size={15} className="text-amber-500 flex-shrink-0" />
                <span className="text-xs text-amber-700">Sin recursos asignados — no es posible calcular la fecha de entrega estimada</span>
              </div>
            )}
          </>
        )}
      </div>

      {/* Stats row */}
      <div className="grid grid-cols-4 gap-3">
        {[["Work Items", workitems.length], ["Completados", completedWI], ["Horas Est.", horasEstDisplay], ["Épicas", epics.length]].map(([label, val]) => (
          <div key={label} className="bg-white rounded-xl border border-gray-100 p-4 text-center" data-testid={`project-stat-${label.toLowerCase().replace(/\s|\./g, "-")}`}>
            <p className="text-2xl font-bold text-[#007934] font-['Outfit']">{val}</p>
            <p className="text-xs text-gray-500 mt-1">{label}</p>
            {label === "Horas Est." && workitemsConEstimacion.length > 0 && workitemsConEstimacion.length < workitems.length && (
              <p className="text-[9px] text-amber-600 mt-0.5">cobertura parcial: {workitemsConEstimacion.length}/{workitems.length} items</p>
            )}
          </div>
        ))}
      </div>

      {/* Reminder */}
      <div className="bg-white rounded-xl border border-gray-100 p-4">
        <h3 className="text-sm font-semibold text-gray-900 mb-3 flex items-center gap-2"><Bell size={16} className="text-[#007934]" />Enviar recordatorio</h3>
        <div className="flex gap-2">
          <input value={reminderEmail} onChange={e => setReminderEmail(e.target.value)} type="email" placeholder="Email del responsable"
            className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="reminder-email-input" />
          <button onClick={handleSendReminder} disabled={sending || !reminderEmail}
            className="bg-[#007934] text-white px-4 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center gap-2" data-testid="send-reminder-btn">
            {sending ? <Loader2 size={14} className="animate-spin" /> : <Send size={14} />} Enviar
          </button>
        </div>
      </div>

      {/* Tabs: Work Items | Sprints | Milestones */}
      <div className="bg-white rounded-xl border border-gray-100">
        <div className="flex border-b border-gray-100">
          {[
            { key: "workitems",  label: "Work Items",  icon: Layers,     count: workitems.length },
            { key: "sprints",    label: "Sprints",     icon: GitBranch,  count: sprints.length },
            { key: "milestones", label: "Hitos",       icon: Target,     count: milestones.length },
            { key: "meetings",   label: "Reuniones",   icon: Calendar,   count: 0 },
          ].map(tab => {
            const Icon = tab.icon;
            return (
              <button key={tab.key} onClick={() => setActiveTab(tab.key)}
                className={`flex items-center gap-2 px-5 py-3.5 text-sm font-medium transition-all border-b-2 ${activeTab === tab.key ? "border-[#007934] text-[#007934]" : "border-transparent text-gray-500 hover:text-gray-700"}`}
                data-testid={`tab-${tab.key}`}>
                <Icon size={15} /> {tab.label}
                {tab.count > 0 && <span className={`text-xs px-1.5 py-0.5 rounded-full ${activeTab === tab.key ? "bg-[#E8F5E9] text-[#007934]" : "bg-gray-100 text-gray-500"}`}>{tab.count}</span>}
              </button>
            );
          })}
        </div>

        {/* ─────── WORK ITEMS TAB ─────── */}
        {activeTab === "workitems" && (
          <div className="p-5">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">Work Items ({workitems.length})</h2>
              <div className="flex items-center gap-2">
                <div className="flex items-center bg-gray-100 rounded-lg p-0.5 text-xs">
                  <button onClick={() => setViewMode("list")} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md transition-all ${viewMode==="list" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`} data-testid="view-list-btn">
                    <List size={12} /> Lista
                  </button>
                  <button onClick={() => setViewMode("kanban")} className={`flex items-center gap-1 px-2.5 py-1.5 rounded-md transition-all ${viewMode==="kanban" ? "bg-white text-gray-800 shadow-sm" : "text-gray-500"}`} data-testid="view-kanban-btn">
                    <LayoutGrid size={12} /> Kanban
                  </button>
                </div>
                <button onClick={() => setShowWIModal(true)} className="flex items-center gap-2 bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]" data-testid="add-workitem-btn">
                  <Plus size={14} /> Añadir
                </button>
              </div>
            </div>
            {workitems.length === 0 ? (
              <div className="text-center py-12 text-gray-400">
                <Layers size={32} className="mx-auto mb-2 opacity-30" />
                <p className="text-sm">No hay work items. Añade el primero.</p>
              </div>
            ) : viewMode === "kanban" ? (
              <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 overflow-x-auto" data-testid="kanban-board">
                {KANBAN_COLS.map(col => {
                  const colItems = workitems.filter(w => col.statuses.includes(w.status));
                  return (
                    <div key={col.key} className={`rounded-xl border-2 transition-colors ${dragId ? "border-dashed" : ""} ${col.headerCls.split(" ")[0].replace("bg-","border-")} bg-gray-50`}
                      onDragOver={e => e.preventDefault()}
                      onDrop={async () => {
                        if (!dragId) return;
                        const wi = workitems.find(w => w.id === dragId);
                        if (!wi || col.statuses.includes(wi.status)) { setDragId(null); return; }
                        try {
                          await axios.put(`${API}/workitems/${dragId}`, { status: col.newStatus });
                          setWorkitems(prev => prev.map(w => w.id === dragId ? {...w, status: col.newStatus} : w));
                        } catch (e) { console.error(e); }
                        setDragId(null);
                      }}
                      data-testid={`kanban-col-${col.key}`}>
                      <div className={`flex items-center justify-between px-3 py-2.5 rounded-t-xl border-b ${col.headerCls}`}>
                        <div className="flex items-center gap-1.5">
                          <span className={`w-2 h-2 rounded-full ${col.dotCls}`} />
                          <span className="text-xs font-semibold">{col.label}</span>
                        </div>
                        <span className="text-xs font-bold">{colItems.length}</span>
                      </div>
                      <div className="p-2 space-y-2 min-h-[120px] max-h-[500px] overflow-y-auto">
                        {colItems.map(wi => {
                          const Icon = typeIcon[wi.type] || CheckSquare;
                          return (
                            <div key={wi.id} draggable onDragStart={() => setDragId(wi.id)} onDragEnd={() => setDragId(null)}
                              className={`bg-white rounded-lg border border-gray-100 p-2.5 cursor-grab hover:shadow-sm hover:border-[#007934]/30 transition-all ${dragId===wi.id ? "opacity-40" : ""}`}
                              onClick={() => handleOpenWIPanel(wi)}
                              data-testid={`kanban-card-${wi.id}`}>
                              <div className="flex items-start gap-1.5 mb-1.5">
                                <div className="w-5 h-5 rounded bg-[#E8F5E9] flex items-center justify-center flex-shrink-0 mt-0.5">
                                  <Icon size={11} className="text-[#007934]" />
                                </div>
                                <p className="text-xs font-medium text-gray-900 leading-tight line-clamp-2">{wi.title}</p>
                              </div>
                              <div className="flex items-center justify-between">
                                <span className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${priorityColor[wi.priority] || "bg-gray-100 text-gray-500"}`}>{wi.priority}</span>
                                <div className="flex items-center gap-1">
                                  {wi.azure_id && <span className="text-[10px] text-blue-400 font-mono">#{wi.azure_id}</span>}
                                  {wi.estimated_hours > 0 && <span className="text-[10px] text-gray-400">{wi.estimated_hours}h</span>}
                                  {wi.assigned_to && <div className="w-5 h-5 rounded-full bg-[#007934]/10 flex items-center justify-center text-[10px] font-bold text-[#007934]">{wi.assigned_to.split(" ").map(n=>n[0]).join("").slice(0,2).toUpperCase()}</div>}
                                </div>
                              </div>
                            </div>
                          );
                        })}
                        {colItems.length === 0 && <div className="text-center py-6 text-gray-300 text-xs">Arrastra aquí</div>}
                      </div>
                    </div>
                  );
                })}
              </div>
            ) : (
              <div className="space-y-1.5">
                {workitems.map(wi => {
                  const Icon = typeIcon[wi.type] || CheckSquare;
                  const isDone = ["Cerrado","Closed","Done","Completado","Removed"].includes(wi.status);
                  return (
                    <div key={wi.id}
                      className="flex items-center gap-3 px-3 py-2.5 rounded-lg border border-gray-100 hover:border-[#007934]/30 hover:bg-[#F0FDF4]/50 transition-all group cursor-pointer"
                      onClick={() => handleOpenWIPanel(wi)}
                      data-testid={`workitem-${wi.id}`}>
                      {/* Quick complete button */}
                      <button
                        onClick={(e) => handleQuickCompleteItem(e, wi)}
                        title={isDone ? "Completado" : "Marcar como completado"}
                        className={`flex-shrink-0 w-5 h-5 rounded-full border-2 flex items-center justify-center transition-all ${isDone ? "border-[#007934] bg-[#007934]" : "border-gray-300 hover:border-[#007934]"}`}
                        data-testid={`complete-wi-${wi.id}`}>
                        {isDone && <CheckCircle2 size={11} className="text-white" />}
                      </button>
                      <div className="flex-shrink-0 w-6 h-6 rounded bg-[#E8F5E9] flex items-center justify-center">
                        <Icon size={12} className="text-[#007934]" />
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className={`text-sm font-medium ${isDone ? "line-through text-gray-400" : "text-gray-900"}`}>{wi.title}</p>
                          <span className="text-xs bg-gray-100 text-gray-500 px-1.5 py-0.5 rounded">{wi.type}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${statusColor[wi.status] || "bg-gray-100 text-gray-600"}`}>{wi.status}</span>
                          <span className={`text-xs px-1.5 py-0.5 rounded-full ${priorityColor[wi.priority] || ""}`}>{wi.priority}</span>
                          {wi.azure_id && <span className="text-[10px] text-blue-500 bg-blue-50 px-1.5 py-0.5 rounded font-mono">#{wi.azure_id}</span>}
                          {wi.devops_synced_at && <span className="text-[10px] text-green-600 bg-green-50 px-1.5 py-0.5 rounded">DevOps synced</span>}
                        </div>
                        <div className="flex items-center gap-3 mt-0.5">
                          {wi.assigned_to && <span className="text-xs text-gray-500">{wi.assigned_to}</span>}
                          {wi.estimated_hours > 0 && <span className="text-xs text-gray-400">{wi.estimated_hours}h est.</span>}
                          {wi.remaining_work > 0 && <span className="text-xs text-amber-500">{wi.remaining_work}h restantes</span>}
                          {wi.story_points > 0 && <span className="text-xs text-gray-400">{wi.story_points} SP</span>}
                          {wi.target_date && <span className="text-xs text-gray-400 flex items-center gap-1"><Calendar size={9} />{wi.target_date}</span>}
                        </div>
                      </div>
                      <div className="flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-all">
                        <button onClick={(e) => { e.stopPropagation(); handleDeleteWI(wi.id); }} className="p-1 text-gray-300 hover:text-red-500 transition-all rounded" data-testid={`delete-wi-${wi.id}`}>
                          <Trash2 size={13} />
                        </button>
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─────── SPRINTS TAB ─────── */}
        {activeTab === "sprints" && (
          <div className="p-5 space-y-4">
            <div className="flex items-center justify-between">
              <h2 className="text-lg font-bold text-gray-900 font-['Outfit']">Gestión de Sprints</h2>
              <button onClick={() => { setSprintForm({ name: "", number: sprints.length + 1, status: "planning", start_date: "", end_date: "", goal: "" }); setShowSprintModal(true); }}
                className="flex items-center gap-2 bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#006528]" data-testid="add-sprint-btn">
                <Plus size={14} /> Nuevo Sprint
              </button>
            </div>

            {/* Velocity chart */}
            {velocityData.length > 1 && (
              <div className="bg-gray-50 rounded-xl border border-gray-100 p-4" data-testid="velocity-chart">
                <div className="flex items-center gap-2 mb-3">
                  <TrendingDown size={14} className="text-[#007934]" />
                  <h3 className="text-sm font-semibold text-gray-700">Velocity Chart — Tendencia del equipo</h3>
                </div>
                <ResponsiveContainer width="100%" height={180}>
                  <ComposedChart data={velocityData} margin={{ top: 4, right: 16, left: 0, bottom: 4 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="#f3f4f6" />
                    <XAxis dataKey="sprint_name" tick={{ fontSize: 10, fill: "#9ca3af" }} />
                    <YAxis tick={{ fontSize: 10, fill: "#9ca3af" }} allowDecimals={false} />
                    <Tooltip contentStyle={{ borderRadius: "8px", border: "1px solid #e5e7eb", fontSize: "11px" }}
                      formatter={(v, n) => [v, n === "total" ? "Planificados" : n === "completed" ? "Completados" : "Velocidad %"]} />
                    <Legend iconSize={8} wrapperStyle={{ fontSize: "10px" }} />
                    <Bar dataKey="total" name="Planificados" fill="#E8F5E9" radius={[3,3,0,0]} />
                    <Bar dataKey="completed" name="Completados" fill="#007934" radius={[3,3,0,0]} />
                    <Line type="monotone" dataKey="completion_pct" name="Velocidad %" stroke="#f59e0b" strokeWidth={2} dot={{ r: 3, fill: "#f59e0b" }} />
                  </ComposedChart>
                </ResponsiveContainer>
              </div>
            )}

            {!sprintBoard ? (
              <div className="flex items-center justify-center py-12"><Loader2 className="animate-spin text-[#007934]" size={28} /></div>
            ) : sprintBoard.sprints.length === 0 ? (
              <div className="text-center py-14 text-gray-400">
                <GitBranch size={36} className="mx-auto mb-3 opacity-20" />
                <p className="text-sm font-medium">Sin sprints creados</p>
                <p className="text-xs mt-1">Crea el primer sprint para organizar el trabajo</p>
              </div>
            ) : (
              <div className="space-y-5">
                {/* Backlog column */}
                <div className="bg-gray-50 rounded-xl border border-gray-200"
                  onDragOver={e => e.preventDefault()}
                  onDrop={() => handleDropOnSprint("")}
                  data-testid="sprint-backlog">
                  <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200">
                    <span className="text-sm font-semibold text-gray-600 flex items-center gap-2"><Layers size={14} /> Backlog sin asignar</span>
                    <span className="text-xs bg-gray-200 text-gray-600 px-2 py-0.5 rounded-full">{sprintBoard.backlog.length}</span>
                  </div>
                  <div className="p-3 space-y-1.5 max-h-48 overflow-y-auto">
                    {sprintBoard.backlog.slice(0,20).map(wi => (
                      <div key={wi.id} draggable onDragStart={() => setSprintDragId(wi.id)} onDragEnd={() => setSprintDragId(null)}
                        className="flex items-center gap-2 bg-white rounded-lg px-3 py-2 border border-gray-100 cursor-grab text-sm hover:border-gray-200"
                        data-testid={`backlog-wi-${wi.id}`}>
                        <span className={`text-[10px] px-1.5 py-0.5 rounded ${priorityColor[wi.priority] || "bg-gray-100 text-gray-500"}`}>{wi.priority}</span>
                        <span className="truncate text-gray-700">{wi.title}</span>
                        <span className="ml-auto text-xs text-gray-400 flex-shrink-0">{wi.status}</span>
                      </div>
                    ))}
                    {sprintBoard.backlog.length === 0 && <p className="text-xs text-gray-400 text-center py-3">Sin items en backlog</p>}
                    {sprintBoard.backlog.length > 20 && <p className="text-xs text-gray-400 text-center">+{sprintBoard.backlog.length-20} más</p>}
                  </div>
                </div>

                {/* Sprint cards */}
                {sprintBoard.sprints.map(sprint => {
                  const cfg = SPRINT_STATUS_CFG[sprint.status] || SPRINT_STATUS_CFG.planning;
                  const done = sprint.work_items.filter(w => ["Closed","closed","Cerrado","Done","Completado","Removed"].includes(w.status)).length;
                  const total = sprint.work_items.length;
                  const pct = total > 0 ? Math.round(done/total*100) : 0;
                  return (
                    <div key={sprint.id} className="border border-gray-200 rounded-xl overflow-hidden"
                      onDragOver={e => { e.preventDefault(); setSprintDragTarget(sprint.id); }}
                      onDragLeave={() => setSprintDragTarget(null)}
                      onDrop={() => { handleDropOnSprint(sprint.id); setSprintDragTarget(null); }}
                      style={{ outline: sprintDragTarget === sprint.id ? "2px dashed #007934" : "none" }}
                      data-testid={`sprint-${sprint.id}`}>
                      <div className="flex items-start justify-between px-4 py-3 bg-gray-50 border-b border-gray-200">
                        <div className="flex-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-bold text-gray-900">Sprint {sprint.number}: {sprint.name}</h3>
                            <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${cfg.cls}`}>{cfg.label}</span>
                          </div>
                          {sprint.goal && <p className="text-xs text-gray-500 mt-0.5">{sprint.goal}</p>}
                          <div className="flex items-center gap-3 mt-1.5">
                            {sprint.start_date && <span className="text-xs text-gray-400 flex items-center gap-1"><Calendar size={10} />{sprint.start_date} → {sprint.end_date}</span>}
                            <span className="text-xs text-gray-400">{total} items · {done} completados ({pct}%)</span>
                          </div>
                          {total > 0 && (
                            <div className="h-1.5 bg-gray-200 rounded-full mt-2 w-48">
                              <div className="h-full bg-[#007934] rounded-full" style={{ width: `${pct}%` }} />
                            </div>
                          )}
                        </div>
                        <div className="flex items-center gap-2 ml-4">
                          <button onClick={() => handleOpenBurndown(sprint)}
                            className="flex items-center gap-1.5 px-2.5 py-1 text-xs border border-gray-200 rounded-lg text-gray-600 hover:border-[#007934] hover:text-[#007934] transition-colors"
                            data-testid={`burndown-btn-${sprint.id}`}>
                            <TrendingDown size={12} /> Burndown
                          </button>
                          <select value={sprint.status} onChange={e => handleUpdateSprintStatus(sprint.id, e.target.value)}
                            className="text-xs border border-gray-200 rounded-lg px-2 py-1 focus:outline-none focus:border-[#007934]"
                            data-testid={`sprint-status-${sprint.id}`}>
                            <option value="planning">Planificación</option>
                            <option value="active">Activo</option>
                            <option value="completed">Completado</option>
                          </select>
                          <button onClick={() => handleDeleteSprint(sprint.id)} className="p-1 text-gray-300 hover:text-red-500 transition-colors" data-testid={`delete-sprint-${sprint.id}`}>
                            <Trash2 size={14} />
                          </button>
                        </div>
                      </div>
                      {/* Sprint kanban */}
                      <div className="grid grid-cols-4 gap-0 divide-x divide-gray-100">
                        {KANBAN_COLS.map(col => {
                          const colItems = sprint.work_items.filter(w => col.statuses.includes(w.status));
                          return (
                            <div key={col.key} className="p-2">
                              <div className="flex items-center gap-1.5 mb-2">
                                <span className={`w-1.5 h-1.5 rounded-full ${col.dotCls}`} />
                                <span className="text-xs font-medium text-gray-500">{col.label}</span>
                                <span className="text-xs text-gray-400 ml-auto">{colItems.length}</span>
                              </div>
                              <div className="space-y-1.5 min-h-[40px]">
                                {colItems.map(wi => (
                                  <div key={wi.id} draggable onDragStart={() => setSprintDragId(wi.id)} onDragEnd={() => setSprintDragId(null)}
                                    className="bg-white rounded-lg px-2.5 py-1.5 border border-gray-100 cursor-grab text-xs hover:border-gray-200 hover:shadow-sm">
                                    <p className="text-gray-800 leading-tight line-clamp-2">{wi.title}</p>
                                    <span className={`text-[10px] ${priorityColor[wi.priority] || "text-gray-400"}`}>{wi.priority}</span>
                                  </div>
                                ))}
                                {colItems.length === 0 && <div className="text-center py-2 text-gray-200 text-[10px]">Vacío</div>}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* ─── BURNDOWN CHART PANEL ─── */}
        {activeTab === "sprints" && burndownSprint && (
          <ProjectBurndownPanel
            burndownSprint={burndownSprint}
            burndownData={burndownData}
            burndownLoading={burndownLoading}
            onClose={() => { setBurndownSprint(null); setBurndownData(null); }}
          />
        )}
        {activeTab === "milestones" && (
          <ProjectMilestones
            milestones={milestones}
            saving={saving}
            onAdd={() => { setEditingMS(null); setMsForm({ title:"", description:"", due_date:"", status:"pending" }); setShowMSModal(true); }}
            onEdit={openEditMS}
            onDelete={handleDeleteMS}
            onUpdateStatus={handleUpdateMSStatus}
          />
        )}
        {activeTab === "meetings" && (
          <ProjectMeetings projectId={project?.id || id}/>
        )}
      </div>

      {/* ── WI Modal ── */}
      {showWIModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" data-testid="add-workitem-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold font-['Outfit']">Nuevo Work Item</h2>
              <button onClick={() => setShowWIModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreateWI} className="p-5 space-y-3">
              <input name="title" value={wiForm.title} onChange={e => setWiForm({...wiForm, title: e.target.value})} required placeholder="Título *"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="wi-title-input" />
              <div className="grid grid-cols-2 gap-3">
                <select value={wiForm.type} onChange={e => setWiForm({...wiForm, type: e.target.value})} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="wi-type-select">
                  {["Epic","Feature","User Story","Bug","Task","Tarea"].map(t => <option key={t}>{t}</option>)}
                </select>
                <select value={wiForm.priority} onChange={e => setWiForm({...wiForm, priority: e.target.value})} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                  {["critica","alta","media","baja"].map(p => <option key={p}>{p}</option>)}
                </select>
                <select value={wiForm.status} onChange={e => setWiForm({...wiForm, status: e.target.value})} className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                  {["Nuevo","Activo","Resuelto","Cerrado"].map(s => <option key={s}>{s}</option>)}
                </select>
                <input placeholder="Asignado a" value={wiForm.assigned_to} onChange={e => setWiForm({...wiForm, assigned_to: e.target.value})}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                <input type="number" placeholder="Horas est." value={wiForm.estimated_hours} onChange={e => setWiForm({...wiForm, estimated_hours: parseInt(e.target.value)||0})}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                <select value={wiForm.sprint_id || ""} onChange={e => setWiForm({...wiForm, sprint_id: e.target.value})}
                  className="border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                  <option value="">Sin sprint</option>
                  {sprints.map(s => <option key={s.id} value={s.id}>Sprint {s.number}: {s.name}</option>)}
                </select>
              </div>
              <textarea placeholder="Descripción" value={wiForm.description} onChange={e => setWiForm({...wiForm, description: e.target.value})} rows={2}
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" />
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowWIModal(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2 text-sm font-medium">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-[#007934] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="save-wi-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null} Crear
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Sprint Modal ── */}
      {showSprintModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" data-testid="add-sprint-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold font-['Outfit']">Nuevo Sprint</h2>
              <button onClick={() => setShowSprintModal(false)}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleCreateSprint} className="p-5 space-y-3">
              <div className="grid grid-cols-2 gap-3">
                <input value={sprintForm.name} onChange={e => setSprintForm({...sprintForm, name: e.target.value})} required placeholder="Nombre del sprint *"
                  className="col-span-2 w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="sprint-name-input" />
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Número de sprint</label>
                  <input type="number" value={sprintForm.number} onChange={e => setSprintForm({...sprintForm, number: parseInt(e.target.value)||1})} min={1}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Estado</label>
                  <select value={sprintForm.status} onChange={e => setSprintForm({...sprintForm, status: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    <option value="planning">Planificación</option>
                    <option value="active">Activo</option>
                    <option value="completed">Completado</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Fecha inicio</label>
                  <input type="date" value={sprintForm.start_date} onChange={e => setSprintForm({...sprintForm, start_date: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="sprint-start-date" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Fecha fin</label>
                  <input type="date" value={sprintForm.end_date} onChange={e => setSprintForm({...sprintForm, end_date: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="sprint-end-date" />
                </div>
              </div>
              <textarea value={sprintForm.goal} onChange={e => setSprintForm({...sprintForm, goal: e.target.value})} rows={2} placeholder="Objetivo del sprint"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" />
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => setShowSprintModal(false)} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2 text-sm font-medium">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-[#007934] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="save-sprint-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null} Crear
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ── Milestone Modal ── */}
      {showMSModal && (
        <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-2xl w-full max-w-md shadow-2xl" data-testid="milestone-modal">
            <div className="flex items-center justify-between p-5 border-b">
              <h2 className="text-lg font-bold font-['Outfit']">{editingMS ? "Editar Hito" : "Nuevo Hito"}</h2>
              <button onClick={() => { setShowMSModal(false); setEditingMS(null); }}><X size={20} className="text-gray-400" /></button>
            </div>
            <form onSubmit={handleSaveMS} className="p-5 space-y-3">
              <input value={msForm.title} onChange={e => setMsForm({...msForm, title: e.target.value})} required placeholder="Título del hito *"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="ms-title-input" />
              <textarea value={msForm.description} onChange={e => setMsForm({...msForm, description: e.target.value})} rows={2} placeholder="Descripción"
                className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none" />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Fecha límite</label>
                  <input type="date" value={msForm.due_date} onChange={e => setMsForm({...msForm, due_date: e.target.value})} required
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="ms-due-date" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Estado</label>
                  <select value={msForm.status} onChange={e => setMsForm({...msForm, status: e.target.value})}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" data-testid="ms-status-select">
                    <option value="pending">Pendiente</option>
                    <option value="in_progress">En progreso</option>
                    <option value="completed">Completado</option>
                    <option value="delayed">Retrasado</option>
                  </select>
                </div>
              </div>
              <div className="flex gap-2 pt-1">
                <button type="button" onClick={() => { setShowMSModal(false); setEditingMS(null); }} className="flex-1 border border-gray-200 text-gray-700 rounded-lg py-2 text-sm font-medium">Cancelar</button>
                <button type="submit" disabled={saving} className="flex-1 bg-[#007934] text-white rounded-lg py-2 text-sm font-medium hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2" data-testid="save-ms-btn">
                  {saving ? <Loader2 size={14} className="animate-spin" /> : null} {editingMS ? "Guardar" : "Crear"}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* ══════════════════════════════════════════════
          Work Item Detail Slide-over Panel (PM view)
          ══════════════════════════════════════════════ */}
      {selectedWI && (
        <div className="fixed inset-0 z-50 flex justify-end" data-testid="wi-detail-panel">
          {/* Backdrop */}
          <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={handleCloseWIPanel} />
          {/* Panel */}
          <div className="relative w-full max-w-lg bg-white shadow-2xl flex flex-col h-full overflow-hidden animate-slide-in-right">
            {/* ── Header ── */}
            <div className="flex items-center justify-between px-5 py-4 border-b border-gray-100 bg-white">
              <div className="flex items-center gap-2.5 min-w-0">
                {(() => { const Icon = typeIcon[wiEditForm.type] || CheckSquare; return (
                  <div className="w-8 h-8 rounded-lg bg-[#E8F5E9] flex items-center justify-center flex-shrink-0">
                    <Icon size={16} className="text-[#007934]" />
                  </div>
                ); })()}
                <div className="min-w-0">
                  <p className="text-xs text-gray-400 uppercase tracking-wider">{wiEditForm.type}</p>
                  {wiEditForm.azure_id && (
                    <span className="text-xs bg-blue-50 text-blue-600 px-1.5 py-0.5 rounded font-mono">Azure #{wiEditForm.azure_id}</span>
                  )}
                </div>
              </div>
              <button onClick={handleCloseWIPanel} className="p-1.5 text-gray-400 hover:text-gray-700 hover:bg-gray-100 rounded-lg transition-all">
                <X size={18} />
              </button>
            </div>

            {/* ── Body (scrollable) ── */}
            <div className="flex-1 overflow-y-auto px-5 py-4 space-y-4">

              {/* Title */}
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1 block">Título</label>
                <input
                  value={wiEditForm.title || ""}
                  onChange={e => setWiEditForm(p => ({ ...p, title: e.target.value }))}
                  className="w-full text-base font-semibold text-gray-900 border border-gray-200 rounded-lg px-3 py-2 focus:outline-none focus:border-[#007934] focus:ring-1 focus:ring-[#007934]/20"
                  data-testid="wi-panel-title" />
              </div>

              {/* Status / Priority / Type row */}
              <div className="grid grid-cols-3 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Estado</label>
                  <select value={wiEditForm.status || ""} onChange={e => setWiEditForm(p => ({ ...p, status: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="wi-panel-status">
                    {["Nuevo","Activo","En curso","Analizando","Resuelto","Cerrado","Completado"].map(s => <option key={s}>{s}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Prioridad</label>
                  <select value={wiEditForm.priority || ""} onChange={e => setWiEditForm(p => ({ ...p, priority: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    {["critica","alta","media","baja"].map(p => <option key={p}>{p}</option>)}
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Tipo</label>
                  <select value={wiEditForm.type || ""} onChange={e => setWiEditForm(p => ({ ...p, type: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    {["Epic","Feature","User Story","Bug","Task","Tarea"].map(t => <option key={t}>{t}</option>)}
                  </select>
                </div>
              </div>

              {/* Assigned + Sprint */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Asignado a</label>
                  <input value={wiEditForm.assigned_to || ""} onChange={e => setWiEditForm(p => ({ ...p, assigned_to: e.target.value }))}
                    placeholder="Nombre o email"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="wi-panel-assigned" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Sprint</label>
                  <select value={wiEditForm.sprint_id || ""} onChange={e => setWiEditForm(p => ({ ...p, sprint_id: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#007934]">
                    <option value="">Sin sprint</option>
                    {sprints.map(s => <option key={s.id} value={s.id}>Sprint {s.number}: {s.name}</option>)}
                  </select>
                </div>
              </div>

              {/* Hours grid */}
              <div className="grid grid-cols-4 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Story Pts</label>
                  <input type="number" min={0} value={wiEditForm.story_points || ""} onChange={e => setWiEditForm(p => ({ ...p, story_points: parseInt(e.target.value)||0 }))}
                    placeholder="0" className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#007934] text-center" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Est. (h)</label>
                  <input type="number" min={0} value={wiEditForm.estimated_hours || ""} onChange={e => setWiEditForm(p => ({ ...p, estimated_hours: parseFloat(e.target.value)||0 }))}
                    placeholder="0" className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#007934] text-center" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Complet. (h)</label>
                  <input type="number" min={0} value={wiEditForm.completed_work || ""} onChange={e => setWiEditForm(p => ({ ...p, completed_work: parseFloat(e.target.value)||0 }))}
                    placeholder="0" className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#007934] text-center" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 block">Restantes (h)</label>
                  <input type="number" min={0} value={wiEditForm.remaining_work || ""} onChange={e => setWiEditForm(p => ({ ...p, remaining_work: parseFloat(e.target.value)||0 }))}
                    placeholder="0" className="w-full border border-gray-200 rounded-lg px-2 py-2 text-sm focus:outline-none focus:border-[#007934] text-center" />
                </div>
              </div>

              {/* Target date + Tags */}
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Calendar size={10} /> Fecha objetivo</label>
                  <input type="date" value={wiEditForm.target_date || ""} onChange={e => setWiEditForm(p => ({ ...p, target_date: e.target.value }))}
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]"
                    data-testid="wi-panel-target-date" />
                </div>
                <div>
                  <label className="text-xs text-gray-500 mb-1 flex items-center gap-1"><Tag size={10} /> Tags</label>
                  <input value={wiEditForm.tags || ""} onChange={e => setWiEditForm(p => ({ ...p, tags: e.target.value }))}
                    placeholder="tag1; tag2"
                    className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934]" />
                </div>
              </div>

              {/* Description */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Descripción</label>
                <textarea value={wiEditForm.description || ""} onChange={e => setWiEditForm(p => ({ ...p, description: e.target.value }))}
                  rows={4} placeholder="Descripción detallada del work item..."
                  className="w-full border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
                  data-testid="wi-panel-description" />
              </div>

              {/* ── Notes / Comentarios ── */}
              <div className="border border-gray-100 rounded-xl overflow-hidden" data-testid="wi-notes-section">
                <div className="flex items-center gap-2 px-4 py-3 bg-gray-50 border-b border-gray-100">
                  <MessageSquare size={13} className="text-[#007934]" />
                  <span className="text-xs font-semibold text-gray-700 uppercase tracking-wider">Notas del equipo</span>
                  {(wiEditForm.notes || []).length > 0 && (
                    <span className="ml-auto text-xs bg-[#E8F5E9] text-[#007934] px-1.5 py-0.5 rounded-full font-semibold">{(wiEditForm.notes || []).length}</span>
                  )}
                </div>

                {/* Existing notes timeline */}
                {(wiEditForm.notes || []).length > 0 && (
                  <div className="divide-y divide-gray-50 max-h-56 overflow-y-auto">
                    {(wiEditForm.notes || []).map(note => {
                      const initials = (note.author || "?").split(" ").map(n => n[0]).join("").slice(0, 2).toUpperCase();
                      const dateStr = note.created_at ? new Date(note.created_at).toLocaleString("es-ES", { day: "2-digit", month: "short", hour: "2-digit", minute: "2-digit" }) : "";
                      return (
                        <div key={note.id} className="group flex items-start gap-3 px-4 py-3 hover:bg-gray-50/80 transition-colors" data-testid={`note-${note.id}`}>
                          <div className="w-7 h-7 rounded-full bg-[#007934]/10 flex items-center justify-center text-[10px] font-bold text-[#007934] flex-shrink-0 mt-0.5">
                            {initials}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 mb-0.5">
                              <span className="text-xs font-semibold text-gray-700">{note.author}</span>
                              <span className="text-[10px] text-gray-400">{dateStr}</span>
                            </div>
                            <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-wrap">{note.text}</p>
                          </div>
                          <button onClick={() => handleDeleteNote(note.id)}
                            className="opacity-0 group-hover:opacity-100 p-1 text-gray-300 hover:text-red-500 transition-all flex-shrink-0"
                            data-testid={`delete-note-${note.id}`}>
                            <Trash2 size={12} />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}

                {/* Add note input */}
                <div className="px-4 py-3 bg-white border-t border-gray-50">
                  <div className="flex gap-2">
                    <textarea
                      value={wiNoteText}
                      onChange={e => setWiNoteText(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) handleAddNote(); }}
                      placeholder="Añadir una nota (Ctrl+Enter para guardar)..."
                      rows={2}
                      className="flex-1 border border-gray-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:border-[#007934] resize-none"
                      data-testid="wi-note-input" />
                    <button onClick={handleAddNote} disabled={wiAddingNote || !wiNoteText.trim()}
                      className="self-end flex items-center gap-1.5 bg-[#007934] text-white px-3 py-2 rounded-lg text-sm font-medium hover:bg-[#006528] disabled:opacity-50 transition-all"
                      data-testid="wi-note-submit">
                      {wiAddingNote ? <Loader2 size={13} className="animate-spin" /> : <Send size={13} />}
                    </button>
                  </div>
                  {(wiEditForm.notes || []).length === 0 && (
                    <p className="text-xs text-gray-400 mt-1.5">Sin notas aún. Sé el primero en añadir un comentario.</p>
                  )}
                </div>
              </div>

              {/* Metadata */}
              <div className="bg-gray-50 rounded-xl p-3 space-y-1.5">
                <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider flex items-center gap-1"><Info size={10} /> Información</p>
                <div className="grid grid-cols-2 gap-x-4 gap-y-1 text-xs text-gray-500">
                  {selectedWI.created_at && <span>Creado: {new Date(selectedWI.created_at).toLocaleDateString("es-ES")}</span>}
                  {selectedWI.updated_at && <span>Modificado: {new Date(selectedWI.updated_at).toLocaleDateString("es-ES")}</span>}
                  {selectedWI.azure_id && <span className="text-blue-500">Azure ID: #{selectedWI.azure_id}</span>}
                  {selectedWI.azure_project && <span className="truncate">Proyecto: {selectedWI.azure_project}</span>}
                  {selectedWI.devops_synced_at && <span className="text-green-600 col-span-2">Sincronizado con DevOps: {new Date(selectedWI.devops_synced_at).toLocaleString("es-ES")}</span>}
                </div>
                {selectedWI.azure_id && (
                  <a href={`https://dev.azure.com/valorizasm/${selectedWI.azure_project || project?.azure_devops_project || ""}/_workitems/edit/${selectedWI.azure_id}`}
                    target="_blank" rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-blue-500 hover:text-blue-700 mt-1"
                    onClick={e => e.stopPropagation()}>
                    <ExternalLink size={11} /> Ver en Azure DevOps
                  </a>
                )}
              </div>

              {/* Sync message */}
              {wiSyncMsg && (
                <div className={`rounded-lg px-3 py-2.5 text-sm flex items-center gap-2 ${wiSyncMsg.ok ? "bg-green-50 text-green-700 border border-green-200" : "bg-red-50 text-red-700 border border-red-200"}`}
                  data-testid="wi-sync-msg">
                  {wiSyncMsg.ok ? <CheckCircle2 size={14} /> : <AlertTriangle size={14} />}
                  {wiSyncMsg.text}
                </div>
              )}
            </div>

            {/* ── Footer Actions ── */}
            <div className="px-5 py-4 border-t border-gray-100 bg-gray-50/50 space-y-2">
              {/* Mark complete */}
              {!["Cerrado","Closed","Done","Completado","Removed"].includes(wiEditForm.status) && (
                <button onClick={handleQuickCompleteFromPanel} disabled={wiPanelSaving}
                  className="w-full flex items-center justify-center gap-2 bg-[#E8F5E9] text-[#007934] py-2.5 rounded-xl text-sm font-semibold hover:bg-[#007934] hover:text-white transition-all disabled:opacity-60"
                  data-testid="wi-panel-complete-btn">
                  <CheckCircle2 size={16} /> Marcar como completado
                </button>
              )}
              {/* Save + Push */}
              <div className="flex gap-2">
                <button onClick={handleSaveWIEdit} disabled={wiPanelSaving}
                  className="flex-1 bg-[#007934] text-white py-2.5 rounded-xl text-sm font-semibold hover:bg-[#006528] disabled:opacity-60 flex items-center justify-center gap-2 transition-all"
                  data-testid="wi-panel-save-btn">
                  {wiPanelSaving ? <Loader2 size={14} className="animate-spin" /> : null}
                  Guardar cambios
                </button>
                {selectedWI.azure_id ? (
                  <button onClick={handlePushToDevops} disabled={wiPushing}
                    className="flex items-center gap-2 border border-blue-200 bg-blue-50 text-blue-700 px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-blue-100 disabled:opacity-60 transition-all"
                    data-testid="wi-panel-devops-btn">
                    {wiPushing ? <Loader2 size={14} className="animate-spin" /> : <CloudUpload size={15} />}
                    Enviar a DevOps
                  </button>
                ) : (
                  <button onClick={handlePublishToDevops} disabled={wiPublishing}
                    className="flex items-center gap-2 border border-violet-200 bg-violet-50 text-violet-700 px-4 py-2.5 rounded-xl text-sm font-semibold hover:bg-violet-100 disabled:opacity-60 transition-all"
                    data-testid="wi-panel-publish-btn"
                    title="Crear este work item en Azure DevOps">
                    {wiPublishing ? <Loader2 size={14} className="animate-spin" /> : <CloudUpload size={15} />}
                    Publicar en DevOps
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
